import { atom, useSetAtom } from 'jotai'
import { useEffect } from 'react'

import { ExtensionInstance, MessageGroup } from '@mangovoice/types'

import {
  DEFAULT_EXTERNAL_TRANSFER_PHONE_NUMBER_DIGIT_LIMITS,
  ExternalTransferPhoneNumberDigitLimits,
  getExternalTransferPhoneNumberDigitLimits,
} from '../utils/externalTransferPhoneNumberValidation'

/**
 * Atoms for reference data shared across switchboard components.
 * These eliminate prop drilling for stable reference data.
 *
 * Set these atoms in the consuming app (e.g., admin3) where data is fetched.
 * Use `useSwitchboardDataAtoms` hook to set atoms from props/data.
 * Components can read from atoms using useAtomValue() instead of receiving props.
 */

/** List of extensions for dropdowns and selectors */
export const extensionsListAtom = atom<ExtensionInstance[]>([])

/** Extension map (ID -> ExtensionInstance) for quick lookups */
export const extensionMapAtom = atom<
  Map<number, ExtensionInstance> | undefined
>(undefined)

/** List of messaging groups for SMS notification nodes */
export const messagingGroupsAtom = atom<MessageGroup[]>([])

/** Whether messaging groups were successfully loaded for the current PBX */
export const messagingGroupsLoadedAtom = atom(false)

export type SoundClipOption = {
  id: string
  uuid?: string
  name: string
}

/** List of sound clips for Sound Clip nodes */
export const soundClipsAtom = atom<SoundClipOption[]>([])

export type ScheduleOption = {
  id: string
  uuid?: string
  name: string
}

/** List of schedules for Schedule nodes */
export const schedulesAtom = atom<ScheduleOption[]>([])

/** List of phone numbers for External Transfer caller ID options */
export const phoneNumbersAtom = atom<
  Array<{ id: string; name: string; number: string }>
>([])

/** Accepted phone-number lengths for External Transfer nodes */
export const externalTransferPhoneNumberDigitLimitsAtom =
  atom<ExternalTransferPhoneNumberDigitLimits>(
    DEFAULT_EXTERNAL_TRANSFER_PHONE_NUMBER_DIGIT_LIMITS,
  )

/** List of SMS-eligible phone numbers for SMS "Send from" fallback options */
export const smsEligiblePhoneNumbersAtom = atom<
  Array<{ id: string; name: string; number: string }>
>([])

/** Current switchboard/dial plan ID (extensionDialPlan) for excluding from Jump to Switchboard dropdown */
export const currentSwitchboardIdAtom = atom<number | undefined>(undefined)

/** AI agent option for the AI Agent node dropdown */
export type AiAgentOption = {
  id: string
  name: string
  title?: string
}

/** List of AI agents for the AI Agent node dropdown (agents without a phone number, scoped to current PBX) */
export const aiAgentsAtom = atom<AiAgentOption[]>([])

/**
 * Hook to set switchboard data atoms from props/data.
 * Call this in the consuming app (e.g., admin3) where data is fetched.
 *
 * @example
 * ```tsx
 * // In admin3 SwitchboardEditPage (after fetching data)
 * useSwitchboardDataAtoms({
 *   extensionsList,
 *   extensionMap,
 *   messagingGroups,
 *   soundClips,
 *   schedules,
 *   phoneNumbers,
 * })
 * ```
 */
export const useSwitchboardDataAtoms = (data: {
  extensionsList?: ExtensionInstance[]
  extensionMap?: Map<number, ExtensionInstance>
  messagingGroups?: MessageGroup[]
  messagingGroupsLoaded?: boolean
  soundClips?: SoundClipOption[]
  schedules?: ScheduleOption[]
  phoneNumbers?: Array<{ id: string; name: string; number: string }>
  smsEligiblePhoneNumbers?: Array<{ id: string; name: string; number: string }>
  currentSwitchboardId?: number
  aiAgents?: AiAgentOption[]
  apiEnvironment?: string
}) => {
  const setExtensionsList = useSetAtom(extensionsListAtom)
  const setExtensionMap = useSetAtom(extensionMapAtom)
  const setMessagingGroups = useSetAtom(messagingGroupsAtom)
  const setMessagingGroupsLoaded = useSetAtom(messagingGroupsLoadedAtom)
  const setSoundClips = useSetAtom(soundClipsAtom)
  const setSchedules = useSetAtom(schedulesAtom)
  const setPhoneNumbers = useSetAtom(phoneNumbersAtom)
  const setSmsEligiblePhoneNumbers = useSetAtom(smsEligiblePhoneNumbersAtom)
  const setCurrentSwitchboardId = useSetAtom(currentSwitchboardIdAtom)
  const setAiAgents = useSetAtom(aiAgentsAtom)
  const setExternalTransferPhoneNumberDigitLimits = useSetAtom(
    externalTransferPhoneNumberDigitLimitsAtom,
  )

  useEffect(() => {
    if (data.extensionsList) {
      setExtensionsList(data.extensionsList)
    }
  }, [data.extensionsList, setExtensionsList])

  useEffect(() => {
    if (data.extensionMap !== undefined) {
      setExtensionMap(data.extensionMap)
    }
  }, [data.extensionMap, setExtensionMap])

  useEffect(() => {
    if (data.messagingGroups) {
      setMessagingGroups(data.messagingGroups)
    }
  }, [data.messagingGroups, setMessagingGroups])

  useEffect(() => {
    if (data.messagingGroupsLoaded !== undefined) {
      setMessagingGroupsLoaded(data.messagingGroupsLoaded)
    }
  }, [data.messagingGroupsLoaded, setMessagingGroupsLoaded])

  useEffect(() => {
    if (data.soundClips) {
      setSoundClips(data.soundClips)
    }
  }, [data.soundClips, setSoundClips])

  useEffect(() => {
    if (data.schedules) {
      setSchedules(data.schedules)
    }
  }, [data.schedules, setSchedules])

  useEffect(() => {
    if (data.phoneNumbers) {
      setPhoneNumbers(data.phoneNumbers)
    }
  }, [data.phoneNumbers, setPhoneNumbers])

  useEffect(() => {
    if (data.smsEligiblePhoneNumbers) {
      setSmsEligiblePhoneNumbers(data.smsEligiblePhoneNumbers)
    }
  }, [data.smsEligiblePhoneNumbers, setSmsEligiblePhoneNumbers])

  useEffect(() => {
    if (data.currentSwitchboardId !== undefined) {
      setCurrentSwitchboardId(data.currentSwitchboardId)
    }
  }, [data.currentSwitchboardId, setCurrentSwitchboardId])

  useEffect(() => {
    if (data.aiAgents) {
      setAiAgents(data.aiAgents)
    }
  }, [data.aiAgents, setAiAgents])

  useEffect(() => {
    setExternalTransferPhoneNumberDigitLimits(
      getExternalTransferPhoneNumberDigitLimits(data.apiEnvironment),
    )
  }, [data.apiEnvironment, setExternalTransferPhoneNumberDigitLimits])
}
